import BaseClass from '@core/common/BaseClass';
import { MEMBER_TYPES } from '@config/Consts';
import UMSConnection from './UMSConnection';

export class UMSConnectionConnectivityError extends Error {
  constructor() {
    super('Not connected to UMS.');
  }
}

export default class UMSClient extends BaseClass {
  /**
   * Returns the connection state if has any.
   */
  get connectionState() {
    return this.umsConnection && this.umsConnection.connectionState;
  }

  /**
   * return the umsClient instance if exists.
   */
  get umsClient() {
    // TODO: if ums client is not available, throw an execption?
    return this.umsConnection && this.umsConnection.umsClient;
  }

  /**
   * Indicate if we are connected to the UMS server.
   */
  get isConnected() {
    return this.umsConnection
      && this.umsConnection.connectionState === this.umsConnection.CONNECTIONS_STATES.CONNECTED;
  }

  /**
   * @constructor
   */
  constructor() {
    super({ name: 'UMSClient', type: 'Client' });
  }

  /**
   * Initialise UMS Client
   * @param config
   * @param {Object} config
   * @param {string} config.accountId - the account id
   * @param {string} config.domain - the UMS domain from csds `CSDSKEY: `
   * @param {string} config.agentToken - the logged in user token
   * @param {string} config.agentId - the logged in user id
   */
  init(config) {
    if (!this.umsConnection) {
      this.umsConnection = new UMSConnection({
        accountId: config.accountId,
        // NOTE: &from=facade param is temporary hack to connect straight to UMS
        // avoiding connection to Facade and resolving websocket issue until a more infrastructured
        // solution is introduced
        apiVersion: '2.1&from=facade',
        domain: config.domain,
        token: config.agentToken,
        memberType: MEMBER_TYPES.AGENT,
      });
      this.accountId = config.accountId;
      this.agentId = config.agentId;

      this.umsConnection.on(
        this.umsConnection.EVENTS.CONNECTION_STATE_CHANGE,
        data => this.onConnectionStateChange(data),
        'UMSConnectionStateChange',
      );

      Object.values(this.umsConnection.EVENTS).forEach((umsConnectionEvent) => {
        this.umsConnection.on(umsConnectionEvent, (event) => {
          this.emit(umsConnectionEvent, event);
        }, `UMSConnection${umsConnectionEvent}`);
      });
    }
  }

  /**
   * Connect to the UMS Server
   */
  async connect() {
    return this.umsConnection.openConnection();
  }

  /**
   * Disconnect from the UMS Server
   */
  disconnect() {
    this.umsConnection.closeConnection();
  }

  /**
   * Handle Connection State Changes
   *
   * @param {Object} data Event Data
   * @param {string} data.newValue the new connection state
   */
  onConnectionStateChange(data) {
    const connectionState = data.newValue;
    // TBD: Do we whant to handle the connection in the client
    // or to allow the service/application layer to handle the flow?

    switch (connectionState) {
      case this.umsConnection.CONNECTIONS_STATES.CONNECTED:
        break;
      case this.umsConnection.CONNECTIONS_STATES.CONNECTING:
        break;
      case this.umsConnection.CONNECTIONS_STATES.DISCONNECTED:
        break;
      case this.umsConnection.CONNECTIONS_STATES.NON_RESPONSIVE:
        break;
      default:
        break;
    }
    this.emit(this.umsConnection.EVENTS.CONNECTION_STATE_CHANGE, connectionState);
  }

  // ///////// API ///////////

  /**
   * Subscribe to Conversations in the UMS Server.
   *
   * @param {Object} payload
   * @param {string} payload.brandId
   * @param {number} [payload.minLastUpdatedTime]
   * @param {string[]} [payload.agentIds]
   * @param {string[]} [payload.groupIds]
   * @param {string} [payload.stage]
   * @param {string} [payload.convState]
   * @param {string} [payload.consumerId]
   * @param {string} [payload.conversationId]
   * @param {number} [payload.maxLastUpdatedTime]
   * @param {number} [payload.maxETTR]
   */
  subscribeConversations(payload) {
    return this.invokeRequest('subscribeConversations', payload);
  }
  /**
   * Unsubscribe from a Conversations in the UMS Server.
   *
   * @param {Object} payload
   * @param {string} [payload.subscriptionId]
   */
  unsubscribeConversations(payload) {
    return this.invokeRequest('unsubscribeConversations', payload);
  }
  /**
   * Query Messages - get all messages of conversations dialog
   * @param {Object} payload
   * @param {string} payload.conversationId - The Conversation ID
   * @param {string} payload.dialogId - The Dialog ID
   * @param {string} payload.maxQuantity - The max quantity of messages to get
   * @param {string} payload.olderThan - The end sequence
   * @param {string} payload.newerThan - The start sequence
   * @param {string} payload.numCallbacks - Times to retry
   */
  newQueryMessages(payload) {
    return this.invokeRequest('newQueryMessages', payload);
  }
  /**
   * Retrive a Participant Profile
   *
   * @param {Object} payload
   * @param {string} payload.userId The agent ID
   */
  getUserProfile(payload) {
    return this.invokeRequest('getUserProfile', payload);
  }

  // ///////// Helpers ///////////

  /**
   * A Generic API Proxy to the client, invoking a method and return a Promise instead of callbacks.
   * in the UMSClient, by name.
   * @param {string} methodName - the name of the function in the UMS to inock
   * @param {Object} payload - The payload object, holds the method arguments
   */
  invokeRequest(methodName, payload) {
    if (!this.isConnected) {
      this.connect().catch(err => this.logError(err));
    }
    return new Promise((resolve, reject) => {
      this.umsClient[methodName]({
        ...payload,
        success: (data) => {
          resolve(data);
        },
        error: (error) => {
          reject(error);
        },
        context: this,
      });
    });
  }
}

const umsClient = new UMSClient();

export { umsClient };
